// ╔══════════════════════════════════════════════════════════════════╗
// ║ ROUTES — o "dicionário" do app (Single Source of Truth).            ║
// ║ Nenhuma path é escrita à mão fora daqui. Componentes/botões/nav     ║
// ║ usam estes builders → o URL e a UI ficam sempre em sincronia.       ║
// ║ Navegação real via History API (createBrowserRouter).              ║
// ║                                                                    ║
// ║ IA de 3 tabs (produto convergido):                                 ║
// ║   • Eventos  — descoberta, bilhetes, carteira (raiz)               ║
// ║   • Tap      — networking/NFC/perfil/compatibilidade (portado)      ║
// ║   • Centro   — consola do organizador (role-aware, prefixo /descobrir) ║
// ║   • Hexis    — copilot flutuante (overlay deep-linkável)            ║
// ╚══════════════════════════════════════════════════════════════════╝

import type { ParseKeys } from 'i18next';

/** Chave válida do catálogo (verificada em compilação contra locales/pt). */
export type MsgKey = ParseKeys;

export const ROUTES = {
  org_onboarding: '/org/onboarding',
  user_onboarding: '/welcome',
  // Onboarding por PASSOS — cada passo é uma rota (deep-linkável, guarda progresso).
  onboardingSteps: ['/welcome', '/welcome/perfil', '/welcome/objetivos', '/welcome/pronto'] as const,

  // ── Tab 1 · EVENTOS (participante / descoberta) ──
  attendee: {
    explore: '/eventos',
    event: (slug: string) => `/eventos/${slug}`,
    tickets: '/bilhetes',
    wallet: '/carteira',
  },

  // ── Tab 2 · TAP (networking — portado da raiz, Fase 4) ──
  tap: {
    root: '/tap',
    user: (id: string) => `/tap/u/${id}`,
    profile: (id: string) => `/perfil/${id}`,
    connections: '/ligacoes',
  },

  // ── Perfil & conta (no Centro) ──
  conta: '/conta',
  perfil: '/conta/perfil',
  telegram: '/conta/telegram',
  definicoes: '/conta/definicoes',
  // ── Descobrir (no Centro) ──
  conteudos: '/conteudos',      // viewer de blog/notícias (todos)
  marketplace: '/marketplace',  // catálogo (todos)
  // Landing de categoria e página de produto — MESMA árvore, para o breadcrumb e
  // o tab ativo continuarem a funcionar sem casos especiais.
  marketCategory: (cat: string) => `/marketplace/c/${cat}`,
  product: (slug: string) => `/marketplace/${slug}`,

  // ── Tab 3 · CENTRO (hub role-aware) ──
  overview: '/descobrir',
  painel: '/descobrir/painel',   // dashboard/charts white-label (Decision Interface)
  // A visão de ecossistema (equipa) vive na consola HQ, fora do www.
  events: {
    root: '/descobrir/eventos',
    new: '/descobrir/eventos/novo',
    kpis: (id: string) => `/descobrir/eventos/${id}/kpis`,
    networking: (id: string) => `/descobrir/eventos/${id}/networking`,
  },
  tickets: {
    sales: '/descobrir/vendas',
    checkin: '/descobrir/checkin',
  },
  stand: '/descobrir/stand',
  // O Estúdio (CMS) vive na consola HQ (/conteudos) — fora do www.
  billing: {
    // Faturação do ORGANIZADOR (planos pro/enterprise) — protegida por permissão.
    plan: '/descobrir/billing/plano',
  },
  // Marcianus+ do LEITOR. Vive fora do /descobrir/billing porque não é a mesma
  // coisa nem é para a mesma pessoa: o PaywallGate mandava toda a gente para a
  // página do organizador, e um leitor normal batia num 403 ao carregar em «Assinar».
  plus: '/marcianus-plus',
  team: {
    members: '/descobrir/equipa/membros',
    branding: '/descobrir/equipa/marca',
  },

  // ── Overlays deep-linkáveis (Fase 5) ──
  hexis: '/hexis',
  notificacoes: '/notificacoes',
} as const;

// ── Tab-bar inferior (3 tabs · Tap ao centro como FAB). Data-driven. ──
export type TabIcon = 'eventos' | 'tap' | 'descobrir';
export interface TabDef {
  key: string;
  to: string;
  label: MsgKey;
  icon: TabIcon;
  eyebrow: MsgKey;       // sobrescrito no header conforme a tab (contexto)
  title: MsgKey;
  fab?: boolean;         // Tap = FAB central dourado
  match: string[];       // prefixos de path que ativam esta tab
}
export const TABS: TabDef[] = [
  { key: 'descobrir', to: ROUTES.overview, label: 'tabs.descobrir.label', icon: 'descobrir',
    eyebrow: 'tabs.descobrir.eyebrow', title: 'tabs.descobrir.title',
    match: ['/descobrir', '/conteudos', '/marketplace'] },
  { key: 'tap', to: ROUTES.tap.root, label: 'tabs.tap.label', icon: 'tap', fab: true,
    eyebrow: 'tabs.tap.eyebrow', title: 'tabs.tap.title',
    match: ['/tap', '/perfil', '/ligacoes'] },
  { key: 'eventos', to: ROUTES.attendee.explore, label: 'tabs.eventos.label', icon: 'eventos',
    eyebrow: 'tabs.eventos.eyebrow', title: 'tabs.eventos.title',
    match: ['/eventos'] },
];

/** Devolve a tab ativa para um pathname (para o realce e o header). */
export function activeTabFor(pathname: string): string | null {
  const hit = TABS.find((t) => t.match.some((p) => pathname === p || pathname.startsWith(p + '/')));
  return hit?.key ?? null;
}
export function tabDefFor(pathname: string): TabDef | undefined {
  const key = activeTabFor(pathname);
  return TABS.find((t) => t.key === key);
}

// ── Header por ROTA — o cabeçalho reflete a tela real (não só a tab). ──
// REGRA (um header, sempre): rotas listadas em ROUTE_HEADER são nomeadas pelo
// app-header → o corpo NÃO repete título (só action-rows). Subtelas de /descobrir
// mostram a tab no app-header + PageHeader próprio no corpo (tab ≠ página).
// eyebrow/title são CHAVES i18n (namespace common) — quem renderiza faz t().
export interface HeaderInfo { eyebrow: MsgKey | ''; title: MsgKey | ''; }
const ROUTE_HEADER: Record<string, HeaderInfo> = {
  '/conta': { eyebrow: 'header.account.eyebrow', title: 'header.account.title' },
  '/conta/perfil': { eyebrow: 'header.profile.eyebrow', title: 'header.profile.title' },
  '/conta/telegram': { eyebrow: 'header.telegram.eyebrow', title: 'header.telegram.title' },
  '/conta/definicoes': { eyebrow: 'header.settings.eyebrow', title: 'header.settings.title' },
  '/bilhetes': { eyebrow: 'header.tickets.eyebrow', title: 'header.tickets.title' },
  '/carteira': { eyebrow: 'header.wallet.eyebrow', title: 'header.wallet.title' },
  '/conteudos': { eyebrow: 'header.content.eyebrow', title: 'header.content.title' },
  '/marketplace': { eyebrow: 'header.marketplace.eyebrow', title: 'header.marketplace.title' },
  '/marcianus-plus': { eyebrow: 'header.plus.eyebrow', title: 'header.plus.title' },
  '/hexis': { eyebrow: 'header.hexis.eyebrow', title: 'header.hexis.title' },
  '/ligacoes': { eyebrow: 'header.connections.eyebrow', title: 'header.connections.title' },
  '/notificacoes': { eyebrow: 'header.notifications.eyebrow', title: 'header.notifications.title' },
};
const DYNAMIC_HEADER: { re: RegExp; header: HeaderInfo }[] = [
  { re: /^\/eventos\/[^/]+$/, header: { eyebrow: 'header.event.eyebrow', title: 'header.event.title' } },
  { re: /^\/conteudos\/[^/]+$/, header: { eyebrow: 'header.contentDetail.eyebrow', title: 'header.contentDetail.title' } },
  { re: /^\/(perfil|tap\/u)\/[^/]+$/, header: { eyebrow: 'header.profileDetail.eyebrow', title: 'header.profileDetail.title' } },
  // A categoria tem de vir ANTES do produto: /marketplace/c/saas casa nas duas
  // expressões, e a primeira a casar ganha. Invertê-las dava a um ecrã de
  // categoria o cabeçalho de um produto.
  { re: /^\/marketplace\/c\/[^/]+$/, header: { eyebrow: 'header.category.eyebrow', title: 'header.category.title' } },
  { re: /^\/marketplace\/[^/]+$/, header: { eyebrow: 'header.product.eyebrow', title: 'header.product.title' } },
];

/** Cabeçalho (eyebrow+título) para um pathname: rota própria > detalhe > tab. */
export function headerFor(pathname: string): HeaderInfo {
  if (ROUTE_HEADER[pathname]) return ROUTE_HEADER[pathname];
  const dyn = DYNAMIC_HEADER.find((d) => d.re.test(pathname));
  if (dyn) return dyn.header;
  const t = tabDefFor(pathname);
  return t ? { eyebrow: t.eyebrow, title: t.title } : { eyebrow: '', title: '' };
}

